if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.content = this.querySelector('.product-media-modal__content');
        this.thumbs = this.querySelector('.product-media-modal__thumbs');
        this.prevButton = this.querySelector('.product-media-modal__arrow--prev');
        this.nextButton = this.querySelector('.product-media-modal__arrow--next');
        this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.index = 0;
        this.pendingIndex = null;
        this.frame = null;

        this.content.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
        ['touchstart', 'wheel', 'pointerdown'].forEach((type) => this.content.addEventListener(type, () => (this.pendingIndex = null), { passive: true }));
        this.content.addEventListener('click', this.onContentClick.bind(this));
        this.content.addEventListener('mousemove', this.onMouseMove.bind(this));
        this.thumbs.addEventListener('click', (event) => {
          const thumb = event.target.closest('button');
          if (thumb) this.goTo(Number(thumb.dataset.index), true);
        });
        this.prevButton.addEventListener('click', () => this.goTo(this.index - 1, true));
        this.nextButton.addEventListener('click', () => this.goTo(this.index + 1, true));
        this.addEventListener('keydown', this.onKeydown.bind(this));

        // the variant picker swaps the slides, so rebuild the thumbnails whenever they change
        new MutationObserver(() => this.refresh()).observe(this.content, { childList: true });
        this.refresh();
      }

      get slides() {
        return Array.from(this.content.querySelectorAll('.product-media-modal__slide:is(:not(.product-media-modal__slide--variant), :first-child)'));
      }

      refresh() {
        const slides = this.slides;
        this.thumbs.replaceChildren(
          ...slides.map((slide, i) => {
            const thumb = document.createElement('button');
            const image = document.createElement('img');
            thumb.type = 'button';
            thumb.dataset.index = i;
            thumb.setAttribute('aria-label', slide.dataset.label);
            image.src = slide.dataset.thumb;
            image.alt = '';
            image.width = 120;
            image.height = 120;
            image.loading = 'lazy';
            thumb.append(image);
            return thumb;
          }),
        );
        slides.forEach((slide, i) => {
          slide.setAttribute('role', 'group');
          slide.setAttribute('aria-roledescription', 'slide');
          slide.setAttribute('aria-label', `${i + 1} / ${slides.length}`);
        });

        const multiple = slides.length > 1;
        this.thumbs.hidden = !multiple;
        this.prevButton.hidden = !multiple;
        this.nextButton.hidden = !multiple;
        this.index = Math.min(this.index, Math.max(slides.length - 1, 0));
        this.update(false);
      }

      show(opener) {
        this.classList.remove('is-closing');
        super.show(opener);

        const slides = this.slides;
        const mediaId = opener?.getAttribute('data-media-id');
        const target = mediaId && this.content.querySelector(`[data-media-id="${mediaId}"]`)?.closest('.product-media-modal__slide');
        this.index = Math.max(slides.indexOf(target), 0);
        this.pendingIndex = null;
        this.update(false);
        slides[this.index]?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'instant' });
        this.loadVideo(slides[this.index]);
      }

      hide() {
        if (!this.hasAttribute('open')) return;
        this.resetZoom();
        super.hide();
        if (this.reducedMotion.matches) return;

        this.classList.add('is-closing');
        const finish = () => this.classList.remove('is-closing');
        this.addEventListener('animationend', (event) => event.target === this && finish(), { once: true });
        setTimeout(finish, 400);
      }

      goTo(index, animate) {
        const slides = this.slides;
        if (index < 0 || index >= slides.length) return;

        this.resetZoom();
        this.index = index;
        this.pendingIndex = animate ? index : null;
        this.update(animate);
        slides[index].scrollIntoView({
          inline: 'center',
          block: 'nearest',
          behavior: animate && !this.reducedMotion.matches ? 'smooth' : 'instant',
        });
        this.loadVideo(slides[index]);
      }

      update(animate) {
        const slides = this.slides;
        const behavior = animate && !this.reducedMotion.matches ? 'smooth' : 'instant';

        // only the current slide and its neighbours fetch their full-size image, and only once opened
        const toLoad = this.hasAttribute('open') ? [this.index - 1, this.index, this.index + 1] : [];
        toLoad.forEach((i) => {
          const image = slides[i]?.querySelector('img[data-srcset]');
          if (!image) return;
          image.srcset = image.dataset.srcset;
          image.removeAttribute('data-srcset');
        });

        Array.from(this.thumbs.children).forEach((thumb, i) => {
          if (i === this.index) {
            thumb.setAttribute('aria-current', 'true');
            thumb.scrollIntoView({ inline: 'center', block: 'nearest', behavior });
          } else {
            thumb.removeAttribute('aria-current');
          }
        });

        this.prevButton.disabled = this.index <= 0;
        this.nextButton.disabled = this.index >= slides.length - 1;
      }

      loadVideo(slide) {
        const media = slide?.querySelector('deferred-media');
        if (media?.querySelector('template')?.content.querySelector('.js-youtube')) media.loadContent(false);
      }

      onScroll() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          const width = this.content.clientWidth;
          if (!width) return;

          const index = Math.round(Math.abs(this.content.scrollLeft) / width);
          if (this.pendingIndex !== null) {
            if (index === this.pendingIndex) this.pendingIndex = null;
            return;
          }
          if (index === this.index) return;

          this.resetZoom();
          this.index = index;
          this.update(true);
        });
      }

      onKeydown(event) {
        if (event.target.closest('video, iframe, model-viewer')) return;
        if (event.key === 'ArrowLeft') this.goTo(this.index + (this.isRtl ? 1 : -1), true);
        else if (event.key === 'ArrowRight') this.goTo(this.index + (this.isRtl ? -1 : 1), true);
        else return;
        event.preventDefault();
      }

      get isRtl() {
        return getComputedStyle(this.content).direction === 'rtl';
      }

      onContentClick(event) {
        if (!this.finePointer.matches) return;

        const image = event.target.closest('.product-media-modal__slide > img');
        if (image) return this.toggleZoom(image.parentElement, event);
        if (event.target.closest('deferred-media, product-model, .product-media-modal__model')) return;
        this.hide();
      }

      toggleZoom(slide, event) {
        if (slide.hasAttribute('data-zoomed')) return this.resetZoom();
        slide.setAttribute('data-zoomed', '');
        this.moveZoom(slide, event);
      }

      onMouseMove(event) {
        const slide = this.content.querySelector('[data-zoomed]');
        if (slide) this.moveZoom(slide, event);
      }

      moveZoom(slide, event) {
        const image = slide.querySelector('img');
        const box = slide.getBoundingClientRect();
        const x = (event.clientX - box.left - image.offsetLeft) / image.offsetWidth;
        const y = (event.clientY - box.top - image.offsetTop) / image.offsetHeight;
        image.style.transformOrigin = `${Math.min(Math.max(x, 0), 1) * 100}% ${Math.min(Math.max(y, 0), 1) * 100}%`;
      }

      resetZoom() {
        this.content.querySelectorAll('[data-zoomed]').forEach((slide) => slide.removeAttribute('data-zoomed'));
      }
    },
  );
}
